/*
 * Paired asset for snippets/product-siblings.liquid.
 *
 * Points the caption under the sibling row at whichever thumbnail the pointer
 * or the keyboard is on, and moves the ring with it. Leaving the row puts both
 * back on the product being viewed.
 *
 * The values are read off the tile's own data attributes rather than looked up
 * in a table, so the server-rendered markup is the single source of truth and
 * this file never has to know what a sibling is.
 *
 * Everything here is an enhancement. Without it the caption names the product
 * being viewed, the ring stays on it, and each thumbnail is still a link to
 * that product - which is also what a touch device gets, since there is no
 * hover to preview with.
 */
(() => {
  if (window.customElements.get('product-siblings')) return;

  class ProductSiblings extends HTMLElement {
    connectedCallback() {
      this.label = this.querySelector('[data-siblings-label]');
      if (!this.label) return;

      this.price = this.querySelector('[data-siblings-price]');
      this.defaultLabel = this.label.textContent;
      this.defaultPrice = this.price ? this.price.textContent : '';
      this.previewed = null;

      this.track = this.querySelector('[data-siblings-track]');
      this.prev = this.querySelector('[data-siblings-prev]');
      this.next = this.querySelector('[data-siblings-next]');
      this.initScroller();

      /*
       * mouseover/focusin rather than mouseenter/focus: those do not bubble, so
       * delegating from the container would mean a listener per tile.
       */
      this.onPreview = this.onPreview.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onBlur = this.onBlur.bind(this);

      this.addEventListener('mouseover', this.onPreview);
      this.addEventListener('focusin', this.onPreview);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusout', this.onBlur);
    }

    onPreview(event) {
      const tile = event.target.closest('[data-sibling-label]');
      /*
       * A null tile is the pointer crossing a gap between thumbnails. The
       * caption deliberately keeps its last value there rather than flicking
       * back to the selected product and forward again on the next tile.
       */
      if (!tile || tile === this.previewed) return;
      this.preview(tile);
    }

    onLeave() {
      this.reset();
    }

    onBlur(event) {
      if (this.contains(event.relatedTarget)) return;
      this.reset();
    }

    preview(tile) {
      this.previewed = tile;
      this.label.textContent = tile.dataset.siblingLabel;
      if (this.price) this.price.textContent = tile.dataset.siblingPrice || '';

      /*
       * Not set when the previewed tile is the selected one, or the ring would
       * fade off the only tile claiming it. See product-siblings.css.
       */
      this.toggleAttribute('data-previewing', !tile.hasAttribute('data-sibling-selected'));
    }

    reset() {
      this.previewed = null;
      this.label.textContent = this.defaultLabel;
      if (this.price) this.price.textContent = this.defaultPrice;
      this.removeAttribute('data-previewing');
    }

    /*
     * The arrows. Swiper is deliberately not involved: the track is an ordinary
     * overflow-x container, so a swipe, a trackpad and these buttons all move
     * the same scrollLeft and there is no second notion of "which slide" to
     * keep in step with it.
     *
     * The buttons reuse two vendor state classes for their styling rather than
     * new ones - `swiper-button-disabled` dims an end that has been reached and
     * `swiper-button-lock` hides the pair when the row already fits, both of
     * which theme.css and vendor.css already draw.
     */
    initScroller() {
      if (!this.track || !this.prev || !this.next) return;

      this.onNav = this.onNav.bind(this);
      this.syncNav = this.syncNav.bind(this);

      this.prev.addEventListener('click', this.onNav);
      this.next.addEventListener('click', this.onNav);
      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

      /*
       * Width-driven, so it has to be re-measured rather than read once: the
       * thumbnails are sized as a fraction of the info column, which changes on
       * resize and again when the sticky info column settles.
       */
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.syncNav);
        this.observer.observe(this.track);
      } else {
        window.addEventListener('resize', this.syncNav);
      }

      this.syncNav();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    onNav(event) {
      const direction = event.currentTarget === this.prev ? -1 : 1;
      /*
       * A page at a time, which with the fractional thumbnail widths lands on a
       * thumbnail boundary on its own - no scroll-snap needed, and none used,
       * since mandatory snapping can refuse to rest at the true end.
       */
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncNav();
      });
    }

    syncNav() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      // Sub-pixel track widths mean the ends are never exactly 0 and max.
      const fits = max <= 1;

      this.prev.classList.toggle('swiper-button-lock', fits);
      this.next.classList.toggle('swiper-button-lock', fits);
      if (fits) return;

      this.prev.classList.toggle('swiper-button-disabled', this.track.scrollLeft <= 1);
      this.next.classList.toggle('swiper-button-disabled', this.track.scrollLeft >= max - 1);
    }
  }

  window.customElements.define('product-siblings', ProductSiblings);
})();
